import React, {
  useState,
  useCallback,
  useMemo,
  useTransition,
} from "react";
import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { useAnimations } from "../hooks/useScrollAnimation";
import {
  motion,
  AnimatePresence,
} from "framer-motion";

gsap.registerPlugin(ScrollTrigger);
import { styles } from "../style";
import { experiences } from "../constants";
import { SectionWrapper } from "../hoc";
import { textVariant, fadeIn } from "../utils/motion";

const ExperienceCard = React.memo(
  ({ experience, isActive, onClick, index }) => {
    return (
      <motion.div
        variants={fadeIn("right", "spring", index * 0.1, 0.5)}
        className={`flex items-center p-4 rounded-lg cursor-pointer transition-all duration-300 ${isActive ? "bg-tertiary" : "bg-primary"
          }`}
        onClick={onClick}
        onKeyDown={(e) => e.key === "Enter" && onClick()}
        role="button"
        tabIndex={0}
        aria-selected={isActive}
        aria-label={`${experience.title} at ${experience.company_name}`}
      >
        <div
          className="flex-shrink-0 w-16 h-16 rounded-full overflow-hidden mr-4 flex items-center justify-center"
          style={{ backgroundColor: experience.iconBg || "transparent" }}
        >
          <img
            src={experience.icon}
            alt={experience.company_name}
            className="w-10 h-10 object-contain"
          />
        </div>
        <div>
          <h3 className="text-white text-[18px] font-bold">
            {experience.title}
          </h3>
          <p className="text-secondary text-[14px]">
            {experience.company_name}
          </p>
        </div>
      </motion.div>
    );
  },
);

const ExperienceDetails = React.memo(({ experience }) => {
  return (
    <motion.div
      key={experience.company_name}
      initial={{ opacity: 0, x: 20 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: -20 }}
      transition={{ duration: 0.2, ease: "easeInOut" }}
      className="bg-tertiary p-8 rounded-lg"
    >
      <h3 className="text-white text-[24px] font-bold mb-4">
        {experience.title}
      </h3>
      <p className="text-secondary text-[16px] mb-4">
        {experience.company_name}
      </p>
      <p className="text-white-100 text-[14px] mb-4">{experience.date}</p>
      <ul className="list-disc ml-5 space-y-2">
        {experience.points.map((point, index) => (
          <li
            key={`experience-point-${index}`}
            className="text-white-100 text-[14px] pl-1 tracking-wider"
          >
            {point}
          </li>
        ))}
      </ul>
    </motion.div>
  );
});

const Experience = () => {
  const [activeExperience, setActiveExperience] = useState(0);
  const [isPending, startTransition] = useTransition();

  const { containerRef } = useAnimations(() => {
    gsap.from(".exp-header", {
      scrollTrigger: { trigger: ".exp-header", start: "top 85%" },
      y: 30, opacity: 0, duration: 1, ease: "power3.out", stagger: 0.2,
    });
  });

  const handleExperienceClick = useCallback((index) => {
    startTransition(() => {
      setActiveExperience(index);
    });
  }, []);

  const currentExperience = useMemo(
    () => experiences[activeExperience],
    [activeExperience],
  );

  return (
    <div ref={containerRef}>
      <div className="exp-header">
        <p className={`${styles.sectionSubText} text-center`}>
          My Professional Journey
        </p>
      </div>

      <div className="exp-header">
        <h2 className={`${styles.sectionHeadText} text-center`}>
          Work Experience
        </h2>
      </div>

      <div className="mt-20 flex flex-col md:flex-row gap-10">
        <div className="md:w-1/3">
          <div className="flex flex-col space-y-4">
            {experiences.map((experience, index) => (
              <ExperienceCard
                key={`experience-${index}`}
                experience={experience}
                isActive={index === activeExperience}
                onClick={() => handleExperienceClick(index)}
                index={index}
              />
            ))}
          </div>
        </div>
        <div className="md:w-2/3">
          <AnimatePresence mode="wait" initial={false}>
            {!isPending && (
              <ExperienceDetails
                key={currentExperience.company_name}
                experience={currentExperience}
              />
            )}
          </AnimatePresence>
        </div>
      </div>
    </div>
  );
};

export default SectionWrapper(Experience, "work");
